/**
 * FirmLogo — renders a company logo via DuckDuckGo → Google faviconV2
 * → sector Lucide icon cascade.
 *
 * Domain resolution priority:
 *   1. Backend `/api/firm-domains` map (fetched once, cached module-
 *      level). This is populated by `scripts/backfill_firm_domains.py`
 *      via Serper — canonical source of truth.
 *   2. Hand-tuned `DOMAIN_OVERRIDES` for pre-seeded top firms.
 *   3. Slug-guess (`{slug}.com`) as last-resort fallback.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";

// Module-level cache so we hit `/api/firm-domains` at most once per
// page load regardless of how many FirmLogo instances mount.
let _domainMap = null;
let _domainMapPromise = null;

function loadDomainMap() {
  if (_domainMap) return Promise.resolve(_domainMap);
  if (_domainMapPromise) return _domainMapPromise;
  _domainMapPromise = api
    .get("/firm-domains")
    .then((r) => {
      _domainMap = r.data?.domains || {};
      return _domainMap;
    })
    .catch(() => {
      _domainMap = {};
      return _domainMap;
    });
  return _domainMapPromise;
}


// Slug → domain overrides for firms where slug != slug.com. Keep this
// list short and add rows only when Clearbit visibly 404s in the UI —
// the fallback icon handles the long tail.
const DOMAIN_OVERRIDES = {
  "aib":                "aib.ie",
  "allied-irish-banks": "aib.ie",
  "jpmorgan":           "jpmorganchase.com",
  "jp-morgan":          "jpmorganchase.com",
  "morgan-stanley":     "morganstanley.com",
  "goldman-sachs":      "goldman.com",
  "bank-of-america":    "bankofamerica.com",
  "bank-of-england":    "bankofengland.co.uk",
  "hsbc":               "hsbc.com",
  "barclays":           "home.barclays",
  "deutsche-bank":      "db.com",
  "credit-suisse":      "credit-suisse.com",
  "bnp-paribas":        "bnpparibas.com",
  "citi":               "citigroup.com",
  "citigroup":          "citigroup.com",
  "wells-fargo":        "wellsfargo.com",
  "american-express":   "americanexpress.com",
  "mckinsey":           "mckinsey.com",
  "bain":               "bain.com",
  "bcg":                "bcg.com",
  "boston-consulting-group": "bcg.com",
  "deloitte":           "deloitte.com",
  "ey":                 "ey.com",
  "kpmg":               "kpmg.com",
  "pwc":                "pwc.com",
  "accenture":          "accenture.com",
  "microsoft":          "microsoft.com",
  "google":             "google.com",
  "meta":               "meta.com",
  "amazon":             "amazon.com",
  "apple":              "apple.com",
  "netflix":            "netflix.com",
  "stripe":             "stripe.com",
  "airbnb":             "airbnb.com",
  "airbnb-london-office": "airbnb.com",
  "uber":               "uber.com",
  "adobe":              "adobe.com",
  "adyen":              "adyen.com",
  "aig":                "aig.com",
  "airbus":             "airbus.com",
  "airtable":           "airtable.com",
  "alan":               "alan.com",
  "allianz":            "allianz.com",
  "alstom":             "alstom.com",
  "amd":                "amd.com",
  "american-airlines":  "aa.com",
  "aecom":              "aecom.com",
  "affirm":             "affirm.com",
  "almac-group":        "almacgroup.com",
  "alvarez-marsal":     "alvarezandmarsal.com",
  "alvarez-and-marsal": "alvarezandmarsal.com",
  "clifford-chance":    "cliffordchance.com",
  "linklaters":         "linklaters.com",
  "freshfields":        "freshfields.com",
  "slaughter-and-may":  "slaughterandmay.com",
  "allen-and-overy":    "allenovery.com",
  "hogan-lovells":      "hoganlovells.com",
  "eversheds-sutherland": "eversheds-sutherland.com",
  "herbert-smith-freehills": "hsf.com",
  "dla-piper":          "dlapiper.com",
  "sidley-austin":      "sidley.com",
  "sullivan-cromwell":  "sullcrom.com",
  "kirkland-ellis":     "kirkland.com",
  "latham-watkins":     "lw.com",
  "weil-gotshal":       "weil.com",
  "macfarlanes":        "macfarlanes.com",
  "white-and-case":     "whitecase.com",
  "arup":               "arup.com",
  "aecom-uk":           "aecom.com",
  "atkins":             "atkinsglobal.com",
  "mott-macdonald":     "mottmac.com",
  "wsp":                "wsp.com",
};

function guessDomain(slug) {
  if (!slug) return null;
  const override = DOMAIN_OVERRIDES[slug];
  if (override) return override;
  // Strip common location suffixes ("-london-office", "-uk", "-us")
  const cleaned = slug
    .replace(/-(london|new-york|paris|dublin|hong-kong|singapore)(-office)?$/i, "")
    .replace(/-(uk|us|eu|hq)$/i, "");
  return `${cleaned}.com`;
}

export default function FirmLogo({ slug, name, sector, IconFallback, size = 36 }) {
  // Domain is resolved via 3-tier cascade: backend map → overrides →
  // slug-guess. State to trigger re-render when the async map loads.
  const [remoteDomain, setRemoteDomain] = useState(_domainMap?.[slug]);
  useEffect(() => {
    if (_domainMap) return;
    loadDomainMap().then((map) => setRemoteDomain(map[slug]));
  }, [slug]);
  const domain = remoteDomain || guessDomain(slug);

  // Two-provider cascade: try DuckDuckGo first (usually higher-res
  // 32-64px PNG), and on placeholder/404 fall back to Google's
  // faviconV2 endpoint (different sourcing — some brands have a
  // Google favicon but no DDG entry, e.g. AIB). Only after BOTH
  // fail do we surface the sector Lucide icon.
  const [providerIdx, setProviderIdx] = useState(0);
  const [failed, setFailed] = useState(false);
  const px = `${size}px`;

  const providers = domain ? [
    `https://icons.duckduckgo.com/ip3/${domain}.ico`,
    `https://www.google.com/s2/favicons?domain=${domain}&sz=128`,
  ] : [];

  const escalate = () => {
    if (providerIdx < providers.length - 1) setProviderIdx((i) => i + 1);
    else setFailed(true);
  };

  if (!domain || failed) {
    return (
      <span
        className="shrink-0 rounded-lg bg-[#F5F0FF] text-[#4C1D95] flex items-center justify-center"
        style={{ width: px, height: px }}
        aria-hidden
      >
        <IconFallback className="h-4 w-4" strokeWidth={2} />
      </span>
    );
  }

  return (
    <span
      className="shrink-0 rounded-lg bg-white border border-stone-100 flex items-center justify-center overflow-hidden"
      style={{ width: px, height: px }}
      data-testid={`firm-logo-${slug}`}
    >
      <img
        key={providerIdx}  // force reload on provider change
        src={providers[providerIdx]}
        alt={`${name || slug} logo`}
        loading="lazy"
        width={size}
        height={size}
        onError={escalate}
        onLoad={(e) => {
          // Both providers serve tiny (16-24px) placeholders instead
          // of proper 404s when a domain has no favicon. Escalate to
          // the next provider or the sector icon.
          const w = e.currentTarget.naturalWidth;
          if (w && w < 20) escalate();
        }}
        // object-contain keeps a rectangular Airbus mark from looking
        // squashed inside a square tile.
        className="w-full h-full object-contain p-1"
      />
    </span>
  );
}
